/**
 * Public hook for reading the admin-configurable Landing Hero copy.
 *
 * Falls back to the frontend-known defaults if the API is unreachable so the
 * marketing page never renders empty state. React-Query caches the result for
 * five minutes; admin edits bust the cache via `["landing-hero"]`.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface HeroBullet { text: string }
export interface HeroStat { value: string; label: string }

export interface LandingHeroConfig {
  badge_text: string;
  headline_prefix: string;
  headline_accent: string;
  headline_suffix: string;
  subcopy: string;
  bullets: HeroBullet[];
  primary_cta_label: string;
  primary_cta_href: string;
  secondary_cta_label: string;
  secondary_cta_href: string;
  problem_headline_prefix: string;
  problem_headline_accent: string;
  problem_subcopy: string;
  problem_stats: HeroStat[];
}

export const DEFAULT_HERO: LandingHeroConfig = {
  badge_text: "14-day Pro trial · No card required · Cancel anytime",
  headline_prefix: "Every comment is a signal.\n",
  headline_accent: "The Baba",
  headline_suffix: " turns the noise into revenue.",
  subcopy:
    "SociBaba's AI reads, tags and replies to every DM and comment across **15+ networks**. Capture leads. Neutralise complaints. Publish everywhere in one click. **Without hiring an army.**",
  bullets: [
    { text: "Real-time YouTube webhooks" },
    { text: "Claude Sonnet 4.5 AI in the loop" },
    { text: "INR + USD billing" },
  ],
  primary_cta_label: "Start free trial",
  primary_cta_href: "/signup",
  secondary_cta_label: "See pricing",
  secondary_cta_href: "/pricing",
  problem_headline_prefix: "You're drowning in comments.\n",
  problem_headline_accent: "Your competitors are closing the leads hidden in them.",
  problem_subcopy:
    "Every minute a buying-intent comment goes unanswered, another customer moves on. Every complaint you miss becomes a public rant. The Baba sees all — so you don't have to.",
  problem_stats: [
    { value: "37%", label: "of buying-intent comments never get a reply" },
    { value: "2.4 hrs", label: "average manual comment triage per day" },
    { value: "1.6 sec", label: "SociBaba's real-time signal latency" },
  ],
};

export function useLandingHero(): LandingHeroConfig {
  const q = useQuery<LandingHeroConfig>({
    queryKey: ["landing-hero"],
    queryFn: async () => (await api.get("/landing-hero")).data,
    staleTime: 5 * 60_000,
  });
  return q.data || DEFAULT_HERO;
}

/**
 * Render a `**bold**` markdown-lite string as a React fragment with <b> spans.
 * Non-token text is emitted as plain strings so the surrounding element
 * decides typography. Escapes are not supported — kept intentionally minimal.
 */
export function renderInlineBold(text: string): React.ReactNode[] {
  if (!text) return [];
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <b key={i} className="text-[color:var(--sb-text)]">{p.slice(2, -2)}</b>
      );
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}
